import { AdapterIODirection, AdapterReviewSeverity, type AdapterReviewCaseRecord } from '@navio/nsdk';
import { URLKeys, type OrderingKeyOfUrlKey } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, type NoddiColumnDef } from 'noddi-ui-web';
import {
  ADAPTER_IO_DIRECTION_LABEL_BY_VALUE,
  ADAPTER_IO_ENTITY_TYPE_LABEL_BY_VALUE,
  ADAPTER_REVIEW_SEVERITY_LABEL_BY_VALUE,
  formatConstantCaseToSentenceCase
} from 'noddi-util';
import { dateOverTimeCell } from './adapterIoListCells';

const severityVariant: Record<AdapterReviewSeverity, 'destructive' | 'warning' | 'outline'> = {
  [AdapterReviewSeverity.CRITICAL]: 'destructive',
  [AdapterReviewSeverity.ERROR]: 'destructive',
  [AdapterReviewSeverity.WARNING]: 'warning',
  [AdapterReviewSeverity.INFO]: 'outline'
};

type AdapterReviewCaseOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getAdapterReviewCases>;

export function getAdapterReviewCasesColumns({
  showBookingDateTime = false
}: {
  showBookingDateTime?: boolean;
} = {}): NoddiColumnDef<AdapterReviewCaseRecord, unknown, AdapterReviewCaseOrderingKey>[] {
  return [
    {
      header: 'Severity',
      accessorKey: 'severity',
      meta: { orderingKey: 'severity' },
      cell: ({ row }) => (
        <NoddiBadge variant={severityVariant[row.original.severity]}>
          {ADAPTER_REVIEW_SEVERITY_LABEL_BY_VALUE[row.original.severity]}
        </NoddiBadge>
      )
    },
    {
      header: 'Failure',
      accessorKey: 'failureReason',
      cell: ({ row }) => {
        const { failureReason, failureReasonLabel } = row.original;
        const reasonLabel = failureReasonLabel || formatConstantCaseToSentenceCase(failureReason);
        return (
          <div className='flex flex-col'>
            <span>{reasonLabel}</span>
            <span className='text-xs text-muted-foreground'>{row.original.entityType}</span>
          </div>
        );
      }
    },
    {
      header: 'Entity',
      accessorKey: 'entityType',
      cell: ({ row }) => (
        <NoddiBadge variant='outline'>
          {ADAPTER_IO_ENTITY_TYPE_LABEL_BY_VALUE[row.original.entityType] ?? row.original.entityType}
        </NoddiBadge>
      )
    },
    {
      header: 'Direction',
      accessorKey: 'directions',
      cell: ({ row }) => (
        <div className='flex flex-wrap gap-1'>
          {row.original.directions.map((direction) => (
            <NoddiBadge key={direction} variant='outline'>
              <NoddiIcon name={direction === AdapterIODirection.PULL ? 'Download' : 'Upload'} size='small' />
              {ADAPTER_IO_DIRECTION_LABEL_BY_VALUE[direction]}
            </NoddiBadge>
          ))}
        </div>
      )
    },
    {
      header: 'Subject',
      accessorKey: 'bookingId',
      cell: ({ row }) => row.original.bookingId ?? row.original.externalId ?? null
    },
    ...(showBookingDateTime
      ? ([
          {
            header: 'Booking date/time',
            accessorKey: 'deliveryWindowStartsAt',
            size: 110,
            cell: ({ row }) => dateOverTimeCell(row.original.deliveryWindowStartsAt),
            meta: { orderingKey: 'booking_date_proximity' }
          }
        ] satisfies NoddiColumnDef<AdapterReviewCaseRecord, unknown, AdapterReviewCaseOrderingKey>[])
      : []),
    {
      header: 'Last detected',
      accessorKey: 'lastDetectedAt',
      size: 110,
      cell: ({ row }) => dateOverTimeCell(row.original.lastDetectedAt),
      meta: { orderingKey: 'last_detected_at' }
    },
    {
      header: 'Detail',
      accessorKey: 'detail',
      cell: ({ row }) => (
        <span className='line-clamp-2 max-w-md text-xs whitespace-pre-wrap' title={row.original.detail}>
          {row.original.detail}
        </span>
      )
    }
  ];
}
